<template lang="pug">
  .wt-entity-table
    .level
      .level-left
        .level-item
          WtTitle(:card-title="!hasLargeTitle" :card-title-large="hasLargeTitle" v-if="showTitle") {{ title }}
        .level-item
          WtTooltip(v-if="tooltipText" :text="tooltipText")
      .level-right
        slot(name="headerRight")

    BField.search-map-field(type="is-primary" v-if="enableSearch")
      BInput(
        :placeholder="searchPlaceholder"
        custom-class="is-rounded"
        type="search"
        icon="magnify"
        v-model="query"
        @input="$emit('search', query)"
      )

    BTable(
      :loading="loading || isLoading"
      :data="filteredList"
      :paginated="total > perPage"
      :backend-pagination="!!paginationObject"
      :total="total"
      :per-page="perPage"
      :current-page="currentPage"
      @page-change="onPageChange"
      :default-sort="[defaultSortField, defaultSortDirection]"
      :backend-sorting="!!paginationObject"
      @sort="onSort"
      :striped="isStriped"
      aria-next-label="Next page"
      aria-previous-label="Previous page"
      aria-page-label="Page"
      aria-current-label="Current page"
    )
      slot
      BTableColumn(centered :visible="enableRemove" width="8rem" v-slot="props")
        WtButton(is-text bold @click.native="$emit('remove', props.row)") REMOVE
      BTableColumn(
        :visible="enableAdd"
        centered
        :field="enableAddField"
        :label="enableAddLabel"
        width="8rem"
        v-slot="props"
      )
        WtButton(
          is-text
          bold
          @click.native="$emit('add', props.row);"
          :disabled="stageEntityButton(props.row).disabled"
        ) {{ stageEntityButton(props.row).text }}
      template(v-slot:empty)
        div(v-if="showEmptyMessage") {{ emptyMessage }}
</template>

<script>
import { mapGetters } from "vuex";
import tablePropsMixin from "@/mixins/tablePropsMixin";
import WtButton from "@@/WtButton/WtButton";
import WtTitle from "@@/WtTitle/WtTitle";
import WtTooltip from "@@/WtTooltip/WtTooltip";

/**
 * Table component designed to work with any entity
 * throughout the site, usually works best if you
 * create a wrapper around this table, see
 * MembersTable for an example
 */
export default {
  name: "WtEntityTable",

  components: { WtTooltip, WtTitle, WtButton },

  mixins: [tablePropsMixin],

  props: {
    filter: {
      type: String,
      default: null
    },

    type: {
      type: String,
      default: "challenge"
    },

    entityType: {
      type: String,
      default: "member"
    },

    title: {
      type: String,
      required: true
    },

    hasLargeTitle: {
      type: Boolean,
      default: false
    },

    /**
     * What field to sort on when data loads
     */
    defaultSortField: {
      type: String,
      required: true
    },

    defaultSortDirection: {
      type: String,
      default: "desc"
    },

    data: {
      type: Array,
      required: true
    },

    stagedEntitiesData: {
      type: Array,
      default: () => []
    },

    /**
     * Pass the laravel pagination object form the endpoint
     */
    paginationObject: {
      type: Object,
      default: null
    },

    /**
     * Method for handling backend pagination and sorting
     */
    fetchAction: {
      type: Function,
      default: () => {}
    },

    showEmptyMessage: {
      type: Boolean,
      default: true
    },

    isStriped: {
      type: Boolean,
      default: true
    },

    tooltipText: {
      type: String,
      default: null
    },

    includeByType: {
      type: String,
      default: "invite"
    },

    searchString: {
      type: String,
      default: ""
    }
  },

  data() {
    return {
      query: this.searchString,
      sortField: "",
      sortOrder: "",
      isLoading: false
    };
  },

  computed: {
    ...mapGetters("challengeBuilder", [
      "stagedMembers",
      "stagedTeams",
      "memberMode",
      "teamMode"
    ]),
    ...mapGetters("challenges", ["sentChallengeInvites"]),

    filteredList() {
      if (this.searchFields.length < 1 || !this.query.length) {
        return this.data;
      }

      return this.data.filter(item => {
        let match = false;

        for (let i = 0; i < this.searchFields.length; i += 1) {
          const field = this.searchFields[i];
          const query = this.query.toLowerCase();
          const fieldValue = item[field].toLowerCase();

          if (fieldValue.indexOf(query) >= 0) {
            match = true;
          }
        }

        return match;
      });
    },

    total() {
      if (this.filteredList) {
        return this._.get(
          this.paginationObject,
          "total",
          this.filteredList.length
        );
      }
      return null;
    },

    currentPage() {
      return this._.get(this.paginationObject, "currentPage", 1);
    },

    perPage() {
      return this._.get(this.paginationObject, "perPage", 7);
    },

    enableAddField() {
      return this.includeByType === "add" ? "add" : "invite";
    },

    enableAddLabel() {
      return this.includeByType === "add" ? "Add" : "Invite";
    },

    addFieldText() {
      // note, we disable the button when an entity is 'staged'
      return this.includeByType === "add" ? "ADD" : "INVITE";
    }
  },

  methods: {
    onSort(column, order) {
      // that is, only perform fetch if backend-pagination
      if (
        !this._.isEmpty(this.paginationObject) &&
        column !== this.enableAddField
      ) {
        this.sortField = column;
        this.sortOrder = order;

        const payload = {
          sortColumn: this.sortField,
          sortOrder: this.sortOrder
        };

        if (this.filter) {
          payload.filter = this.filter;
        }

        this.fetchAction(payload).then(response => {
          this.$emit("fetchActionResponse", response);
        });
      }
    },

    sortByAdd(a, b, isAsc) {
      if (isAsc) {
        return !!this.membersTeam(a) - !!this.membersTeam(b);
      } else {
        return !!this.membersTeam(b) - !!this.membersTeam(a);
      }
    },

    onPageChange(page) {
      // that is, only perform fetch if backend-pagination
      if (!this._.isEmpty(this.paginationObject)) {
        const payload = {
          sortColumn: this.sortField,
          sortOrder: this.sortOrder,
          page
        };

        if (this.filter) {
          payload.filter = this.filter;
        }

        this.isLoading = true;
        this.fetchAction(payload).then(response => {
          this.$emit("fetchActionResponse", response);
          this.isLoading = false;
        });
      }
    },

    stageEntityButton(entity) {
      // TODO refactor to use teamStagedMembers teamSentInvites
      const stagedEntities =
        this.entityType === "team" ? this.stagedTeams : this.stagedMembers;

      if (
        this._.find(stagedEntities, {
          id: entity.id
        }) ||
        (this.type === "challenge" &&
          this.includeByType === "invite" &&
          this._.find(this.sentChallengeInvites, {
            recipientId: entity.id
          }))
      ) {
        return { text: this.addFieldText, disabled: true };
      } else {
        return { text: this.addFieldText, disabled: false };
      }
    },

    membersTeam(member) {
      let membersTeam = false;

      this.stagedEntitiesData.forEach(team => {
        if (team.teamMembers.some(teamMember => teamMember.id === member.id))
          membersTeam = team.name;
      });

      return membersTeam;
    }
  }
};
</script>

<style lang="sass"></style>
